import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as submasterService from '../../services/submaster.service';
import * as clientCatalogService from '../../services/clientCatalog.service';
import { TEAR_SHEET } from '../../constant';
import TearSheetPackTableSection from './TearSheetPackTableSection';
import TearSheetDocumentsSection from './TearSheetDocumentsSection';
import TearSheetContainerLoadability from './TearSheetContainerLoadability';
import {
  emptyPackRow,
  normalizePackRows,
  serializePackRows,
  formatDimensions,
  mainImageSrc,
} from './tearSheetUtils';
import '../../pages/ClientCatalog/ClientCatalog.css';

/**
 * Tear sheet modal for a client catalog product line (optional read-only / catalog-preview mode).
 */
const TearSheetModal = ({
  open,
  onClose,
  clientCode,
  catalogProduct,
  selectedClient,
  onSaved,
  readOnly = false,
  hidePCode = false,
}) => {
  const [barcodeTypes, setBarcodeTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loadabilityUoms, setLoadabilityUoms] = useState([]);
  const [skuId, setSkuId] = useState('');
  const [cost, setCost] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [barcodeTypeId, setBarcodeTypeId] = useState('');
  const [status, setStatus] = useState('unapproved');
  const [productName, setProductName] = useState('');
  const [lengthDim, setLengthDim] = useState('');
  const [breadthDim, setBreadthDim] = useState('');
  const [heightDim, setHeightDim] = useState('');
  const [dimensionUnit, setDimensionUnit] = useState('');
  const [netWeight, setNetWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('');
  const [hsCode, setHsCode] = useState('');
  const [moq, setMoq] = useState('');
  const [innerPacks, setInnerPacks] = useState([emptyPackRow()]);
  const [masterPacks, setMasterPacks] = useState([emptyPackRow()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const defaultCurrencyId = useMemo(() => {
    const c = selectedClient?.currency_id ?? selectedClient?.currencyId;
    if (c !== undefined && c !== null && c !== '') return String(c);
    const cur = selectedClient?.currency;
    if (cur?.id) return String(cur.id);
    return '';
  }, [selectedClient]);

  const hydrate = useCallback(
    (p) => {
      if (!p) return;
      setSkuId(p.skuId ?? '');
      const effectiveCost =
        p.tearSheetCost !== undefined && p.tearSheetCost !== null
          ? String(p.tearSheetCost)
          : p.cost !== undefined && p.cost !== null
            ? String(p.cost)
            : '';
      setCost(effectiveCost);
      const cur =
        p.currencyId !== undefined && p.currencyId !== null && p.currencyId !== ''
          ? String(p.currencyId)
          : defaultCurrencyId;
      setCurrencyId(cur);
      const bt =
        p.barcodeTypeId !== undefined && p.barcodeTypeId !== null && p.barcodeTypeId !== ''
          ? String(p.barcodeTypeId)
          : '';
      setBarcodeTypeId(bt);
      setStatus(p.tearSheetStatus === 'approved' ? 'approved' : 'unapproved');
      setInnerPacks(normalizePackRows(p.innerPacks));
      setMasterPacks(normalizePackRows(p.masterPacks));
      setProductName(p.productName ?? '');
      setLengthDim(p.length ?? p.lengthDimension ?? '');
      setBreadthDim(p.breadth ?? p.breadthDimension ?? '');
      setHeightDim(p.height ?? p.heightDimension ?? '');
      setDimensionUnit(p.dimensionUnit ?? p.dimension_unit ?? '');
      setNetWeight(p.netWeight ?? p.net_weight ?? '');
      setWeightUnit(p.weightUnit ?? p.weight_unit ?? '');
      setHsCode(p.hsCode ?? p.hs_code ?? '');
      setMoq(p.moq ?? '');
      setError(null);
    },
    [defaultCurrencyId],
  );

  useEffect(() => {
    if (!open) return;
    const loadSubmasters = async () => {
      try {
        const [bc, cur, load] = await Promise.all([
          submasterService.getItems('barcode-type'),
          submasterService.getItems('currency'),
          submasterService.getItems('uom-loadability'),
        ]);
        setBarcodeTypes(Array.isArray(bc) ? bc.filter((x) => x.active !== false) : []);
        setCurrencies(Array.isArray(cur) ? cur.filter((x) => x.active !== false) : []);
        setLoadabilityUoms(Array.isArray(load) ? load.filter((x) => x.active !== false) : []);
      } catch (e) {
        console.error(e);
      }
    };
    loadSubmasters();
  }, [open]);

  useEffect(() => {
    if (open && catalogProduct) {
      hydrate(catalogProduct);
    }
  }, [open, catalogProduct, hydrate]);

  const handlePackChange = (which, idx, field, value) => {
    const setter = which === 'innerPacks' ? setInnerPacks : setMasterPacks;
    setter((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addPackRow = (which) => {
    const setter = which === 'innerPacks' ? setInnerPacks : setMasterPacks;
    setter((prev) => [...prev, emptyPackRow()]);
  };

  const removePackRow = (which, idx) => {
    const setter = which === 'innerPacks' ? setInnerPacks : setMasterPacks;
    setter((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!clientCode || !catalogProduct?.uid) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        sku_id: skuId.trim() === '' ? null : skuId.trim(),
        tear_sheet_cost: cost === '' ? null : Number(cost),
        currency_id: currencyId === '' ? null : Number(currencyId),
        barcode_type_id: barcodeTypeId === '' ? null : Number(barcodeTypeId),
        status,
        inner_packs: serializePackRows(innerPacks),
        master_packs: serializePackRows(masterPacks),
        // client-catalog specific product overrides
        product_name: productName === '' ? null : productName,
        hs_code: hsCode === '' ? null : hsCode,
        moq: moq === '' ? null : Number(moq),
        length_dimension: lengthDim === '' ? null : Number(lengthDim),
        breadth_dimension: breadthDim === '' ? null : Number(breadthDim),
        height_dimension: heightDim === '' ? null : Number(heightDim),
        dimension_unit: dimensionUnit === '' ? null : dimensionUnit,
        net_weight: netWeight === '' ? null : Number(netWeight),
        weight_unit: weightUnit === '' ? null : weightUnit,
      };
      const updated = await clientCatalogService.updateClientCatalogTearSheet(
        clientCode,
        catalogProduct.uid,
        payload,
      );
      if (typeof onSaved === 'function') {
        onSaved(updated);
      }
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message || TEAR_SHEET.SAVE_FAILED);
    } finally {
      setSaving(false);
    }
  };

  if (!open || !catalogProduct) return null;

  const imgSrc = mainImageSrc(catalogProduct);

  return (
    <div className="modal-backdrop" role="presentation">
      <div
        className="import-modal tear-sheet-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tear-sheet-title"
      >
        <div className="modal-header tear-sheet-modal-header">
          <div className="tear-sheet-header-cluster">
            <div className="tear-sheet-header-main">
              <span className="tear-sheet-eyebrow">
                {readOnly ? TEAR_SHEET.EYEBROW_VIEW_ONLY : TEAR_SHEET.EYEBROW}
              </span>
              <div className="tear-sheet-meta-row" aria-label="Product identifiers">
                <span className="tear-sheet-meta-pill">
                  <span className="tear-sheet-meta-key">Item no</span>
                  <span className="tear-sheet-meta-val">{catalogProduct.itemNo ?? '—'}</span>
                </span>
                {!hidePCode && (
                  <span className="tear-sheet-meta-pill tear-sheet-meta-pill-accent">
                    <span className="tear-sheet-meta-key">P code</span>
                    <span className="tear-sheet-meta-val" title="Client catalog code">
                      {catalogProduct.pCode ?? '—'}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <button type="button" className="btn-close-client" onClick={onClose} aria-label="Close tear sheet">
            ×
          </button>
        </div>

        <div className="tear-sheet-modal-body modal-body">
          {error && (
            <div className="alert alert-danger py-2 px-3 mb-2 small" role="alert">
              {error}
            </div>
          )}

          <section className="tear-sheet-section tear-sheet-section-compact">
            <h5 className="tear-sheet-section-title">{TEAR_SHEET.SECTION_PRODUCT_INFO}</h5>
            <div className="row g-3 tear-sheet-product-info-layout align-items-start">
              <div className="col-12 col-xl-9 order-2 order-xl-1">
                <div className="row g-3 tear-sheet-field-grid">
                  <div className="col-12 col-md-7">
                    <label className="tear-sheet-lbl">Product name</label>
                    <input
                      className="form-control form-control-sm tear-sheet-input-compact"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="—"
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                  </div>
                  <div className="col-12 col-md-5">
                    <label htmlFor="tear-sku-id" className="tear-sheet-lbl">
                      SKU ID
                    </label>
                    <input
                      id="tear-sku-id"
                      className="form-control form-control-sm tear-sheet-input-compact"
                      value={skuId}
                      onChange={(e) => setSkuId(e.target.value)}
                      placeholder="—"
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="tear-sheet-lbl">Dimensions (L × B × H)</label>
                    <div className="d-flex gap-2">
                      <input
                        className="form-control form-control-sm tear-sheet-input-compact"
                        value={lengthDim}
                        onChange={(e) => setLengthDim(e.target.value)}
                        placeholder="L"
                        readOnly={readOnly}
                        disabled={readOnly}
                      />
                      <input
                        className="form-control form-control-sm tear-sheet-input-compact"
                        value={breadthDim}
                        onChange={(e) => setBreadthDim(e.target.value)}
                        placeholder="B"
                        readOnly={readOnly}
                        disabled={readOnly}
                      />
                      <input
                        className="form-control form-control-sm tear-sheet-input-compact"
                        value={heightDim}
                        onChange={(e) => setHeightDim(e.target.value)}
                        placeholder="H"
                        readOnly={readOnly}
                        disabled={readOnly}
                      />
                      <input
                        className="form-control form-control-sm tear-sheet-input-compact"
                        value={dimensionUnit}
                        readOnly
                        disabled
                        aria-label="dimension unit"
                        style={{ maxWidth: '5rem' }}
                      />
                    </div>
                  </div>
                  <div className="col-6 col-sm-3">
                    <label className="tear-sheet-lbl">Net weight</label>
                    <input
                      className="form-control form-control-sm tear-sheet-input-compact"
                      value={netWeight}
                      onChange={(e) => setNetWeight(e.target.value)}
                      placeholder="—"
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                  </div>
                  <div className="col-6 col-sm-3">
                    <label className="tear-sheet-lbl">HS code</label>
                    <input
                      className="form-control form-control-sm tear-sheet-input-compact"
                      value={hsCode}
                      onChange={(e) => setHsCode(e.target.value)}
                      placeholder="—"
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="tear-sheet-lbl">Finish</label>
                    <div className="tear-sheet-tag-box tear-sheet-tag-box-sm">
                      {(catalogProduct.finish || []).length === 0 ? (
                        <span className="text-muted small">—</span>
                      ) : (
                        catalogProduct.finish.map((f) => (
                          <span key={f.id} className="tear-sheet-tag">
                            {f.name}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="tear-sheet-lbl">Color</label>
                    <div className="tear-sheet-tag-box tear-sheet-tag-box-sm">
                      {(catalogProduct.color || []).length === 0 ? (
                        <span className="text-muted small">—</span>
                      ) : (
                        catalogProduct.color.map((c) => (
                          <span key={c.id} className="tear-sheet-tag">
                            {c.name}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-2 d-flex align-items-end">
                    <div style={{ width: '100%' }}>
                      <label className="tear-sheet-lbl">MOQ</label>
                      <input
                        className="form-control form-control-sm tear-sheet-input-compact"
                        value={moq}
                        onChange={(e) => setMoq(e.target.value)}
                        placeholder="—"
                        readOnly={readOnly}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                  <div className="col-6 col-lg-3">
                    <label htmlFor="tear-currency" className="tear-sheet-lbl">
                      Currency
                    </label>
                    <select
                      id="tear-currency"
                      className="form-select form-select-sm tear-sheet-input-compact"
                      value={currencyId}
                      onChange={(e) => setCurrencyId(e.target.value)}
                      disabled={readOnly}
                    >
                      <option value="">—</option>
                      {currencies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.symbol ? `${c.name} (${c.symbol})` : c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6 col-lg-3">
                    <label htmlFor="tear-cost" className="tear-sheet-lbl">
                      Cost
                    </label>
                    <input
                      id="tear-cost"
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control form-control-sm tear-sheet-input-compact"
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                  </div>

                  <div className="col-12 col-sm-6 col-lg-3">
                    <label htmlFor="tear-barcode-type" className="tear-sheet-lbl">
                      Barcode type
                    </label>
                    <select
                      id="tear-barcode-type"
                      className="form-select form-select-sm tear-sheet-input-compact"
                      value={barcodeTypeId}
                      onChange={(e) => setBarcodeTypeId(e.target.value)}
                      disabled={readOnly}
                    >
                      <option value="">—</option>
                      {barcodeTypes.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!readOnly && (
                    <div className="col-12 col-sm-6 col-lg-3">
                      <label htmlFor="tear-status" className="tear-sheet-lbl">
                        Status
                      </label>
                      <select
                        id="tear-status"
                        className="form-select form-select-sm tear-sheet-input-compact"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="unapproved">Unapproved</option>
                        <option value="approved">Approved</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-xl-3 order-1 order-xl-2 tear-sheet-product-image-col">
                <div className="tear-sheet-prod-image-frame">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={
                        catalogProduct.productName ||
                        catalogProduct.itemNo ||
                        'Product image'
                      }
                      className="tear-sheet-prod-image"
                      width={250}
                      height={250}
                    />
                  ) : (
                    <div className="tear-sheet-prod-image-placeholder">
                      <span className="small text-muted">No image</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <TearSheetPackTableSection
            title={TEAR_SHEET.SECTION_INNER_PACK}
            packKey="innerPacks"
            className="tear-sheet-section-compact"
            rows={innerPacks}
            barcodeTypes={barcodeTypes}
            readOnly={readOnly}
            onChange={handlePackChange}
            onAdd={() => addPackRow('innerPacks')}
            onRemove={(idx) => removePackRow('innerPacks', idx)}
          />

          <TearSheetPackTableSection
            title={TEAR_SHEET.SECTION_MASTER_PACK}
            packKey="masterPacks"
            className="tear-sheet-section-compact"
            rows={masterPacks}
            barcodeTypes={barcodeTypes}
            readOnly={readOnly}
            onChange={handlePackChange}
            onAdd={() => addPackRow('masterPacks')}
            onRemove={(idx) => removePackRow('masterPacks', idx)}
          />

          <TearSheetContainerLoadability masterPacks={masterPacks} loadabilityUnits={loadabilityUoms} />

          {!readOnly && (
            <TearSheetDocumentsSection
              clientCode={clientCode}
              catalogProduct={catalogProduct}
              disabled={saving}
              onCatalogUpdated={onSaved}
            />
          )}
        </div>

        <div className="modal-footer tear-sheet-footer border-top bg-white">
          {readOnly ? (
            <button type="button" className="btn btn-primary" onClick={onClose}>
              {TEAR_SHEET.BTN_CLOSE}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
                {TEAR_SHEET.BTN_CANCEL}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? TEAR_SHEET.SAVING : TEAR_SHEET.BTN_SAVE}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TearSheetModal;
